import { Injectable, inject } from '@angular/core';
import { delay, tap, switchMap, take, filter, map, startWith, shareReplay, pairwise, first } from 'rxjs/operators';
import { WsService } from '@app/services/ws.service';
import { ServerMessageType } from '@app/enums/server-message-type';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { combineLatest, fromEvent, iif, merge, of } from 'rxjs';
import { ConnectionStatus } from '@app/enums/connection-status';
import { ServerMsg, ServerMsgConnectResponse } from '@app/models/server-msg';
import { environment } from '@environments/environment';
import { Store, select } from '@ngrx/store';
import * as WSActions from '@app/store/features/web-socket/web-socket.actions';
import * as ConfigActions from '@app/store/features/config/config.actions';
import * as ConfigSelectors from '@app/store/features/config/config.selectors';
import * as AuthSelectors from '@app/store/features/auth/auth.selectors';
import * as AuthActions from '@app/store/features/auth/auth.actions';
import { Router } from '@angular/router';

@Injectable()
export class WSEffects {

    private readonly _ws = inject(WsService);
    private readonly _actions$ = inject(Actions);
    private readonly _store = inject(Store);
    private readonly _router = inject(Router);

    onConnectedStartPingPong$ = this._ws.getServerMsg<ServerMsgConnectResponse>(ServerMessageType.ConnectResponse)
        .pipe(
            // tap(data => console.log("PING start", data)),
            tap(() => this._ws.sendPing()),
            tap(() => this._store.dispatch(WSActions.updatePing())),
            switchMap((() => this._ws.getServerMsg<ServerMsg>(ServerMessageType.Pong))),
            tap(() => this._store.dispatch(WSActions.updatePong())),
            delay(environment.pingPongTime),
            tap(() => this._ws.sendPing()),
            tap(() => this._store.dispatch(WSActions.updatePing()))
        )

    wsPing$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(WSActions.updateConnectionStatus),
                switchMap(({ status }) => iif(() => status === ConnectionStatus.Connected, this.onConnectedStartPingPong$, of())),
            ),
        { dispatch: false }
    );

    wsInitConnection$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(WSActions.connectWebSocket),
                //      delay(600),
                switchMap(({ wsUrl, token, skin }) => this._ws.connect(wsUrl, token, skin))
            ),
        { dispatch: false }
    );


    // CODE_DEBT RestAPI
    wsReconnect$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(WSActions.updateConnectionStatus),
                filter(({ status }) => status === ConnectionStatus.Disconnected),

                switchMap(() => combineLatest([
                    this._store.pipe(
                        select(AuthSelectors.getLoggedInStatus),
                        filter(domainSettings => !!domainSettings),
                        map((domainSettings) => domainSettings!),
                    ),
                    this._store.pipe(
                        select(ConfigSelectors.getDomainSettings),
                        filter(domainSettings => !!domainSettings),
                        map((domainSettings) => domainSettings!),
                    ),
                    this._store.pipe(
                        select(ConfigSelectors.getAuthSettings),
                        filter(authSettings => !!authSettings),
                        map((authSettings) => authSettings!),
                    )])
                ),
                map(([loggedIn, domainSettings, authSettings]) => ({ loggedIn, token: authSettings.token, wsUrl: domainSettings.wsUri, skin: domainSettings.skinName })),
                // tap(() => console.log("🚀 Reconnect")),
                tap(({ loggedIn, token, wsUrl, skin }) => {
                    if (loggedIn) {
                        //  this._ws.connect(wsUrl, token, skin)
                    }
                })
            ),
        { dispatch: false }
    );


    /**
     * Reconnection 
     * Started but its not finished
    */



    onlineStatus$ = merge(
        fromEvent(window, 'online'),
        fromEvent(window, 'offline')
    ).pipe(
        startWith(null),
        map(() => navigator.onLine),
        tap(s => console.log('@online s', s)),
        shareReplay({ bufferSize: 1, refCount: false })
    )

    onOffline$ = createEffect(() =>
        this.onlineStatus$
            .pipe(
                filter(online => !online),
                switchMap(() => this._store.select(ConfigSelectors.getAuthSettings).pipe(first())),
                filter(authSettings => !!authSettings), // only if user is logged 
                tap(() => {
                    this._ws.disconnect();
                }),
                map(() => WSActions.updateConnectionStatus({ status: ConnectionStatus.Offline })) // Clear State Reducer will handle this action
            ));



    internetReconnection$ = createEffect(() =>
        this.onlineStatus$.pipe(
            pairwise(),
            filter(([wasOnline, isOnline]) => !wasOnline && isOnline), // if was Offline and goes Online
            switchMap(() => this._store.select(ConfigSelectors.getAuthSettings).pipe(first())),
            filter(authSettings => !!authSettings), // only if user is logged in and disconnection happen
            map(authSettings => authSettings!),
            tap(() => {
                console.log("🚀 Internet Reconnection");
                this._router.navigate(['/lobby'])
            }),
            delay(1000), // wait for WS connection to estabilish
            map(({ username, password }) => AuthActions.onLoginWithUsernamePasswordFromCredentials({ username, password }))
        ))


    wsDisconnectionReconnection$ = createEffect(() =>
        this._actions$.pipe(
            ofType(WSActions.updateConnectionStatus),
            filter(({ status }) => status === ConnectionStatus.Disconnected),
            switchMap(() => this.onlineStatus$.pipe(first())),
            filter(online => online === true), // only if internet is connected
            switchMap(() => this._store.select(ConfigSelectors.getAuthSettings).pipe(first())),
            filter(authSettings => !!authSettings), // only if user is logged in and disconnection happen
            map(authSettings => authSettings!),
            tap(() => {
                console.log("🚀 WS Disconnection Reconnection");
                this._router.navigate(['/lobby'])
            }),
            map(({ username, password }) => AuthActions.onLoginWithUsernamePasswordFromCredentials({ username, password }))
        )
    );
} 